import { Component, Input } from '@angular/core';
import { Filter, FilterTableSettings } from '@gea/digital-ui-lib';
import { tap } from 'rxjs/operators';
import { GeoChartOptions } from 'src/app/shared/components/geo-chart/geo-chart.component';
import { TableIconComponent } from 'src/app/shared/components/table-icon/table-icon.component';
import { Asset } from 'src/app/shared/data/data.generated';
import { HRTColumnDefinition, SelectedFilters } from 'src/app/shared/data/filterConfigurationTypes';
import { GetCustomTypeText } from 'src/app/shared/data/product-types';
import { tableDefinitions } from 'src/app/shared/data/table-definitions';
import { ApiService } from 'src/app/shared/services/api.service';
import {
  Table,
  TableResult,
  TableView,
} from 'src/app/shared/services/api/table';
import {
  DashboardQueryType,
  DashboardService,
  DashboardServiceCondition,
} from 'src/app/shared/services/dashboard.service';
import { HrtTableService } from 'src/app/shared/services/hrt-table.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { RegionDataService } from 'src/app/shared/services/region-data.service';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
  selector: 'app-dashboard-asset',
  templateUrl: './dashboard-asset.component.html',
  styleUrls: ['./dashboard-asset.component.scss'],
  standalone: false
})
export class DashboardAssetComponent {

  @Input() data: any;
  tableId: string = 'dashboardAsset';
  totalCount: number = 0;
  columnDefinitions: HRTColumnDefinition[];
  tableData: any[];
  listOptions: TableView = new TableView();
  unsortableKeys = ['ProductTypeId', 'ProductTypeIdText'];

  countries: any[] = [];
  assetsType: DashboardQueryType = DashboardQueryType.Assets;
  // default values.
  selectedCountry: string = 'USA';
  presetFilter: SelectedFilters = {
    Country: [
      {
        key: 'USA',
        label: 'United States',
      },
    ],
  };
  defaultTableConfig: FilterTableSettings = {
    pageSize: 20,
    columns: {}
  };
  selectedState: string = '';
  selectedType: DashboardQueryType = DashboardQueryType.Assets;
  selectedCondition: DashboardServiceCondition = DashboardServiceCondition.All;
  selectedProductType: number = -1;
  appliedType: DashboardQueryType = DashboardQueryType.Assets;

  showWorldMap: boolean = true;
  showStateMap: boolean = true;
  tableDataLoading: boolean = false;
  initialLoad: boolean = true;

  mapData: (string | number)[][] = [];
  mapDataLoading: boolean = false;
  worldOptions: GeoChartOptions = GeoChartOptions.WorldViewOptions;
  stateOptions: GeoChartOptions = GeoChartOptions.StateViewOptions;

  getCustomTypeText = GetCustomTypeText;

  assetTable: Table<Asset> = new Table<Asset>(this.loader);

  public displayedColumns: string[] = [
    'Icon',
    'AssetTypeId',
    'GeaReferenceName',
    'Manufacturer',
    'Model',
    'AlternativeModel',
    'SerialNumber',
    'SalesOrderNumber',
    'Annunciation',
    'MotorStatus'
  ];

  constructor(
    private regionData: RegionDataService,
    private api: ApiService,
    private dashboardService: DashboardService,
    private utilityService: UtilityService,
    private hrtTableService: HrtTableService,
    private loader: LoaderService
  ) {}


  ngOnInit(): void {
    this.applyPresetFilters();
    if (sessionStorage.getItem(this.tableId + '-listOptions')) {
      this.listOptions = JSON.parse(
        sessionStorage.getItem(this.tableId + '-listOptions') || ''
      );
    }
    this.assetTable.GetApi = (listOptions: TableView) => {
      this.listOptions = listOptions;
      return this.api.Assets.GetAssets(
        this.listOptions,
        undefined,
        this.selectedCountry,
        this.selectedState,
        this.selectedProductType
      );
    };

    this.assetTable.Updated = () => (this.tableDataLoading = false);

    // For initial load, control doesn't go in tap unless subscribed.
    // this.assetTable.refreshDataForTable()?.subscribe();

    // this.assetTable.Updated = () => (this.tableDataLoading = false);
    this.assignMockColDefinitions();
    this.updateMapAndTable();
    this.utilityService.setActiveTab(0, 'dashboardActiveIndex');
  }

  ngAfterViewInit() {
    this.initialLoad = false;
  }

  assignMockColDefinitions() {
    let definitions = tableDefinitions.assetTableDef;

    this.columnDefinitions = this.hrtTableService.addTypeIconToColumn(
      definitions,
      ['ProductTypeId', 'OperatingStatus'],
      TableIconComponent
    );
  }

  applyPresetFilters() {
    if (!sessionStorage.getItem(this.tableId)) return;
    this.presetFilter = JSON.parse(
      sessionStorage.getItem(this.tableId) || ''
    );
    this.selectedCountry = (this.presetFilter['Country'] && this.presetFilter['Country'].length > 0) ? this.presetFilter['Country'][0].key.toString() : '';
    this.selectedState = (this.presetFilter['State'] && this.presetFilter['State'].length > 0) ? this.presetFilter['State'][0].key.toString() : '';
    
  }

  /**
   * Specific logic for handling cancellation of dashboard filters like Country, State, etc.
   * @param selectedFilters 
   */
  handleSelectedFilters(selectedFilters: SelectedFilters) {
    //console.log('Selected filters in parent:', selectedFilters);
    if (selectedFilters) {
      if (!selectedFilters['Country']) {
        this.selectedCountry = '';
        this.selectedState = '';
      }
      if (!selectedFilters['State']) {
        this.selectedState = '';
      }
      this.updateMapAndTable();
    }
  }

  regionSelected(region: string) {
    if (this.showWorldMap) {
      let lookup2Digit = this.regionData.Countries.find(
        (x) => x.Code2 == region
      );
      let lookupName = this.regionData.Countries.find((x) => x.Name == region);
      lookup2Digit?.Name
        ? this.updateCountryFilter(lookup2Digit.Code3, lookup2Digit.Name)
        : null;
      this.selectedCountry = lookup2Digit?.Code3 ?? lookupName?.Code3 ?? region;
      this.updateMapAndTable();
    } else if (this.showStateMap) {
      const stateName = this.regionData.States.find(
        (x) => x.Code == region
      )?.Name;
      this.selectedState = region;
      if (stateName) this.updateStateFilter(region, stateName);
      this.updateMapAndTable();
    }
  }

  updateCountryFilter(countryCode: string, countryName: string) {
    if (this.presetFilter) {
      this.presetFilter = {
        ...this.presetFilter,
        Country: [
          {
            key: countryCode,
            label: countryName,
          },
        ],
      };
    }
  }

  updateStateFilter(stateCode: string, stateName: string) {
    if (this.presetFilter) {
      this.presetFilter = {
        ...this.presetFilter,
        State: [
          {
            key: stateCode,
            label: stateName,
          },
        ],
      };
    }
  }

  updateMapAndTable() {
    //this.tableDataLoading = true;
    this.stateOptions.colorAxis = { colors: ['lightGray', '#0303B8'] };
    this.worldOptions.colorAxis = { colors: ['lightGray', '#0303B8'] };

    if (this.selectedType !== DashboardQueryType.OperatingData) {
      this.appliedType = this.selectedType;
    }
    if (this.selectedCountry === '' && this.selectedState === '') {
      this.showWorldMap = true;
      this.showStateMap = false;
    } else if (this.selectedCountry === 'USA') {
      //this.selectedState === ""
      this.showWorldMap = false;
      this.showStateMap = true;
    } else {
      this.showWorldMap = false;
      this.showStateMap = false;
    }
    if (this.showWorldMap || this.showStateMap) {
      this.assetTable.oldRequest = {};
      this.mapDataLoading = true;
      this.assetTable.SearchUpdated();
      this.api.Assets.GetAssetMapData(
        this.selectedType,
        this.selectedCountry,
        this.selectedProductType,
        this.selectedCondition
      ).subscribe((x) => {
        this.mapData = this.dashboardService.normalizeCountryData(x);
        this.mapDataLoading = false;
      });
    } else {
      // if country is not USA
      this.assetTable.oldRequest = {};
      this.assetTable.SearchUpdated();
    }
  }

  rowClicked(rowData: any) {
    // console.log('Row click event', rowData);
    this.utilityService.openAsset(rowData.Id);
  }

  exportAsset() {
    const listOptions = this.listOptions;
    // console.log('List options:', listOptions);
    // console.log('Table list options:', this.assetTable.View);
    // return;
    listOptions.Page = 0;
    listOptions.HasPaging = false;
    this.api.Assets.ExportAssets(listOptions,
      undefined,
      this.selectedCountry,
      this.selectedState,
      this.selectedProductType,
      undefined,
      'Asset');
  }

  backToWorld() {
    this.selectedCountry = '';
    this.selectedState = '';
    this.presetFilter = {
      ...this.presetFilter,
      Country: [],
      State: [],
    };
    this.updateMapAndTable();
  }

  ngOnDestroy() {
    sessionStorage.setItem(
      this.tableId + '-listOptions',
      JSON.stringify(this.listOptions)
    );
  }
}
